import { useNavigate, useSearchParams } from "react-router-dom";

import { useEffect } from "react";

import Swal from "sweetalert2";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useServiceCallViewModel } from "../../../viewmodels/operation/servicecall/useServiceCallViewModel";

const getStateName = (state: number) => {
  switch (state) {
    case 0:
      return "Pending";

    case 1:
      return "In Progress";

    case 2:
      return "Completed";

    case 3:
      return "Cancelled";

    case 4:
      return "On Hold";

    case 5:
      return "Rescheduled";

    case 6:
      return "Rejected";

    default:
      return "-";
  }
};

const getStateClass = (state: number) => {
  switch (state) {
    case 0:
      return "bg-yellow-100 text-yellow-700";

    case 1:
      return "bg-blue-100 text-blue-700";

    case 2:
      return "bg-green-100 text-green-700";

    case 3:
      return "bg-red-100 text-red-700";

    case 4:
      return "bg-orange-100 text-orange-700";

    case 5:
      return "bg-purple-100 text-purple-700";

    case 6:
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-slate-100 text-slate-700";
  }
};

const ServiceCallsPage = () => {
  useEffect(() => {
    setPage(Number(searchParams.get("page") ?? "1"));
    setSearch(searchParams.get("search") ?? "");
    setStateFilter(
      searchParams.get("state") ? Number(searchParams.get("state")) : null,
    );
    setUnAssignedTechnician(searchParams.get("unassigned") === "true");
  }, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const changePage = (newPage: number) => {
    setPage(newPage);

    setSearchParams({
      page: String(newPage),
      search,
      state: stateFilter?.toString() ?? "",
      unassigned: String(unAssignedTechnician),
    });
  };

  const {
    serviceCalls,
    loading,
    page,
    setPage,
    search,
    setSearch,
    deleteServiceCall,
    stateFilter,
    setStateFilter,
    unAssignedTechnician,
    setUnAssignedTechnician,
  } = useServiceCallViewModel();

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Service Call?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    await deleteServiceCall(id);

    await Swal.fire({
      icon: "success",
      title: "Deleted",
      text: "Service call deleted successfully.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">Service Calls</h1>

            <p className="text-gray-500 mt-1">Manage service calls</p>
          </div>

          <button
            onClick={() => navigate("/service-calls/create")}
            className="bg-primary text-white px-5 py-3 rounded-2xl font-medium hover:opacity-90 transition"
          >
            + Create
          </button>
        </div>

        {/* FILTERS */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SEARCH */}
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>

              <input
                value={search}
                onChange={(e) => {
                  const value = e.target.value;

                  setSearch(value);
                  setPage(1);

                  setSearchParams({
                    page: "1",
                    search: value,
                    state: stateFilter?.toString() ?? "",
                    unassigned: String(unAssignedTechnician),
                  });
                }}
                placeholder="Customer, technician..."
                className="w-full border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-primary"
              />
            </div>

            {/* STATE */}
            <div>
              <label className="block text-sm font-medium mb-2">State</label>

              <select
                value={stateFilter ?? ""}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? null : Number(e.target.value);

                  setStateFilter(value);
                  setPage(1);

                  setSearchParams({
                    page: "1",
                    search,
                    state: value?.toString() ?? "",
                    unassigned: String(unAssignedTechnician),
                  });
                }}
                className="w-full border border-slate-300 rounded-xl px-4 py-3"
              >
                <option value="">All States</option>

                <option value={0}>Pending</option>

                <option value={1}>In Progress</option>

                <option value={2}>Completed</option>

                <option value={3}>Cancelled</option>

                <option value={4}>On Hold</option>

                <option value={5}>Rescheduled</option>

                <option value={6}>Rejected</option>
              </select>
            </div>

            {/* UNASSIGNED */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Technician
              </label>

              <button
                type="button"
                onClick={() => {
                  const value = !unAssignedTechnician;

                  setUnAssignedTechnician(value);
                  setPage(1);

                  setSearchParams({
                    page: "1",
                    search,
                    state: stateFilter?.toString() ?? "",
                    unassigned: String(value),
                  });
                }}
                className={`w-full border rounded-xl px-4 py-3 text-left transition ${
                  unAssignedTechnician
                    ? "bg-primary text-white border-primary"
                    : "bg-white"
                }`}
              >
                Unassigned Technician:{" "}
                <span className="font-semibold">
                  {unAssignedTechnician ? "ON" : "OFF"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="w-full overflow-x-auto md:overflow-x-visible">
            <table className="w-full md:min-w-0 min-w-[1100px] text-sm text-left table-auto">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    No
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Customer
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Address
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Technician
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    State
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center p-8 border border-slate-200"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : serviceCalls.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center p-8 border border-slate-200 text-gray-500"
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  serviceCalls.map((x: any) => (
                    <tr key={x.id} className="hover:bg-slate-50 transition">
                      {/* NO */}
                      <td className="p-3 border border-slate-200 font-semibold whitespace-nowrap">
                        {x.no}
                      </td>

                      {/* CUSTOMER */}
                      <td className="p-3 border border-slate-200">
                        <div className="font-medium break-words">
                          {x.customerName}
                        </div>
                      </td>

                      {/* ADDRESS */}
                      <td className="p-3 border border-slate-200 break-words">
                        {x.customerAddress || "-"}
                      </td>

                      {/* TECHNICIAN */}
                      <td className="p-3 border border-slate-200 break-words">
                        {x.technicianName || "-"}
                      </td>

                      {/* STATE */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStateClass(
                            x.state,
                          )}`}
                        >
                          {getStateName(x.state)}
                        </span>
                      </td>

                      {/* ACTIONS */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* VIEW */}
                          <button
                            onClick={() =>
                              navigate(`/service-calls/view//${x.id}?page=${page}&search=${encodeURIComponent(search)}&state=${stateFilter ?? ""}&unassigned=${unAssignedTechnician}`)
                            }
                            className="bg-blue-500 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            View
                          </button>

                          {/* EDIT */}
                          <button
                            onClick={() =>
                              navigate(
                                `/service-calls/edit/${x.id}?page=${page}&search=${encodeURIComponent(search)}&state=${stateFilter ?? ""}&unassigned=${unAssignedTechnician}`,
                              )
                            }
                            className="bg-yellow-500 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            Edit
                          </button>

                          {/* DELETE */}
                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            disabled={page === 1}
            onClick={() => changePage(page - 1)}
            className="px-4 py-2 border rounded-xl disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={() => changePage(page + 1)}
            className="px-4 py-2 border rounded-xl"
          >
            Next
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ServiceCallsPage;
